import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useCampaignTrendData, CampaignMetrics } from '@/hooks/useCampaignTrendData';
import SparklineChart from '@/components/pmt3/charts/SparklineChart';

// Column headers matching the Google Sheets layout
const columnHeaders = ['Campaign', 'Clicks', 'Spend', 'Sales', 'CPC', 'ACoS'];

// Component for rendering a campaign row with sparklines
interface CampaignRowProps {
  name: string;
  metrics: CampaignMetrics;
  isLoading: boolean;
  isSubsection?: boolean;
}

const CampaignRow: React.FC<CampaignRowProps> = ({ name, metrics, isLoading, isSubsection = false }) => {
  const cellClass = `text-xs py-2 px-3 ${isSubsection ? 'pl-6' : ''}`;
  const nameClass = `${cellClass} font-semibold ${isSubsection ? 'font-medium' : ''}`;

  return (
    <TableRow>
      <TableCell className={nameClass}>{name}</TableCell>
      <TableCell className={cellClass}>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <SparklineChart data={metrics.clicks} />
        )}
      </TableCell>
      <TableCell className={cellClass}>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <SparklineChart data={metrics.spend} />
        )}
      </TableCell>
      <TableCell className={cellClass}>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <SparklineChart data={metrics.sales} />
        )}
      </TableCell>
      <TableCell className={cellClass}>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <SparklineChart data={metrics.cpc} />
        )}
      </TableCell>
      <TableCell className={cellClass}>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <SparklineChart data={metrics.acos} />
        )}
      </TableCell>
    </TableRow>
  );
};

// Component for section headers
interface SectionHeaderProps {
  title: string;
  colSpan?: number;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, colSpan = 6 }) => (
  <TableRow className="bg-gray-100 dark:bg-gray-700">
    <TableCell
      colSpan={colSpan}
      className="text-lg font-bold py-4 px-3 text-gray-900 dark:text-gray-100 border-t-2 border-gray-300 dark:border-gray-600"
    >
      {title}
    </TableCell>
  </TableRow>
);

// Component for spacer rows
const SpacerRow: React.FC = () => (
  <TableRow>
    {columnHeaders.map((_, idx) => (
      <TableCell key={`spacer-${idx}`} className="py-2"></TableCell>
    ))}
  </TableRow>
);

const CampaignTrendDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = useCampaignTrendData(
    selectedProduct?.companyName,
    selectedProduct?.id
  );

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">4wk Trend / Campaign</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto pb-6">
        {error && (
          <div className="text-red-600 dark:text-red-400 mb-4">
            Error loading data: {error.message}
          </div>
        )}
        
        <Table className="min-w-max">
          <TableHeader>
            <TableRow>
              {columnHeaders.map((header) => (
                <TableHead key={header} className="whitespace-nowrap text-xs py-2 px-3 font-bold">
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Data: 7 Day Results - Aggregated Summary */}
            {data && (
              <CampaignRow
                name="Data: 7 Day Results"
                metrics={data.dataSummary}
                isLoading={isLoading}
              />
            )}
            
            <SpacerRow />
            
            {/* Global Max Bid Section */}
            <SectionHeader title="Global Max Bid" />
            
            {/* Exact Campaigns Subsection */}
            {data && (
              <>
                <CampaignRow name="Exact" metrics={data.globalMaxBid.exact} isLoading={isLoading} />
                <CampaignRow name="TOS Exact" metrics={data.globalMaxBid.tosExact} isLoading={isLoading} />
                <CampaignRow name="ROS Exact" metrics={data.globalMaxBid.rosExact} isLoading={isLoading} />
                <CampaignRow name="PP Exact" metrics={data.globalMaxBid.ppExact} isLoading={isLoading} />
              </>
            )}
            
            <SpacerRow />
            
            {/* PTA Campaigns Subsection */}
            {data && (
              <>
                <CampaignRow name="PTA" metrics={data.globalMaxBid.pta} isLoading={isLoading} />
                <CampaignRow name="TOS PTA" metrics={data.globalMaxBid.tosPta} isLoading={isLoading} />
                <CampaignRow name="ROS PTA" metrics={data.globalMaxBid.rosPta} isLoading={isLoading} />
                <CampaignRow name="PP PTA" metrics={data.globalMaxBid.ppPta} isLoading={isLoading} />
              </>
            )}
            
            <SpacerRow />
            
            {/* Broad/CTA Subsection */}
            {data && (
              <>
                <CampaignRow name="Broad" metrics={data.globalMaxBid.broad} isLoading={isLoading} />
                <CampaignRow name="CTA" metrics={data.globalMaxBid.cta} isLoading={isLoading} />
              </>
            )}
            
            <SpacerRow />
            
            {/* Full Auto Section */}
            <SectionHeader title="Full Auto" />
            {data && (
              <>
                <CampaignRow name="Full Auto Close-Match" metrics={data.fullAuto.closeMatch} isLoading={isLoading} />
                <CampaignRow name="Full Auto Complements" metrics={data.fullAuto.complements} isLoading={isLoading} />
                <CampaignRow name="Full Auto Loose-Match" metrics={data.fullAuto.looseMatch} isLoading={isLoading} />
                <CampaignRow name="Full Auto Substitutes" metrics={data.fullAuto.substitutes} isLoading={isLoading} />
              </>
            )}
            
            <SpacerRow />
            
            {/* CA Auto Section */}
            <SectionHeader title="CA Auto" />
            {data && (
              <>
                <CampaignRow name="CA Auto Close-Match" metrics={data.caAuto.closeMatch} isLoading={isLoading} />
                <CampaignRow name="CA Auto Complements" metrics={data.caAuto.complements} isLoading={isLoading} />
                <CampaignRow name="CA Auto Loose-Match" metrics={data.caAuto.looseMatch} isLoading={isLoading} />
                <CampaignRow name="CA Auto Substitutes" metrics={data.caAuto.substitutes} isLoading={isLoading} />
              </>
            )}
            
            <SpacerRow />
            
            {/* Micro Auto Section */}
            <SectionHeader title="Micro Auto" />
            {data && (
              <>
                <CampaignRow name="Micro Auto Close-Match" metrics={data.microAuto.closeMatch} isLoading={isLoading} />
                <CampaignRow name="Micro Auto Complements" metrics={data.microAuto.complements} isLoading={isLoading} />
                <CampaignRow name="Micro Auto Loose-Match" metrics={data.microAuto.looseMatch} isLoading={isLoading} />
                <CampaignRow name="Micro Auto Substitutes" metrics={data.microAuto.substitutes} isLoading={isLoading} />
              </>
            )}
            
            {/* Final spacer row */}
            <SpacerRow />
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default CampaignTrendDashboard;